/**
 * App-wide icon set: Phosphor Duotone (MIT, via react-icons) - the same style as the sidebar.
 *
 * Exports keep the old lucide-react names (X, Search, Copy, ...) so components only
 * change their import path. To swap an icon everywhere, change its mapping here.
 */
import React from 'react';
import type { IconType } from 'react-icons';
import {
  PiArrowClockwiseDuotone, PiArrowCounterClockwiseDuotone, PiArrowLeftDuotone, PiArrowSquareOutDuotone,
  PiAtDuotone, PiBroadcastDuotone, PiCalendarBlankDuotone, PiCalendarDuotone, PiCameraSlashDuotone,
  PiCaretDoubleLeftDuotone, PiCaretDoubleRightDuotone, PiCaretDownDuotone, PiCaretLeftDuotone,
  PiCaretRightDuotone, PiCaretUpDuotone, PiCheckCircleDuotone, PiCheckDuotone, PiClockDuotone,
  PiCloudDuotone, PiCopyDuotone, PiDownloadSimpleDuotone, PiEnvelopeDuotone, PiEyeDuotone,
  PiFloppyDiskDuotone, PiFolderDuotone, PiGameControllerDuotone, PiGaugeDuotone, PiGearSixDuotone,
  PiGitBranchDuotone, PiGlobeDuotone, PiHardDrivesDuotone, PiHeartbeatDuotone, PiImageDuotone, PiInfoDuotone,
  PiKeyDuotone, PiLightningDuotone, PiLinkDuotone, PiListChecksDuotone, PiListDuotone,
  PiMagnifyingGlassDuotone, PiMapPinDuotone, PiMicrophoneStageDuotone, PiMinusDuotone, PiMonitorDuotone, PiMusicNotesDuotone,
  PiPackageDuotone, PiPaperPlaneTiltDuotone, PiPauseDuotone, PiPencilSimpleDuotone, PiPlayDuotone,
  PiPlusDuotone, PiPowerDuotone, PiPulseDuotone, PiRadioDuotone, PiReceiptDuotone, PiRobotDuotone,
  PiSignInDuotone, PiSignOutDuotone, PiSlidersHorizontalDuotone, PiSparkleDuotone, PiSpinnerGapDuotone,
  PiSquareDuotone, PiStackDuotone, PiTagDuotone, PiTelevisionDuotone, PiTerminalWindowDuotone,
  PiTimerDuotone, PiTrashDuotone, PiUploadSimpleDuotone, PiUserSwitchDuotone, PiUsersDuotone,
  PiUsersThreeDuotone, PiVideoCameraDuotone, PiWarningCircleDuotone, PiWarningDuotone, PiWifiHighDuotone,
  PiWrenchDuotone, PiXCircleDuotone, PiXDuotone,
  PiCheckBold,
} from 'react-icons/pi';

export interface AppIconProps extends React.SVGAttributes<SVGElement> {
  size?: string | number;
  color?: string;
  title?: string;
  /** lucide-react compatibility. Phosphor icons are filled shapes, so this is ignored -
   * except on icons given a bold variant, where >= 2.5 switches to it (e.g. a heavy check). */
  strokeWidth?: string | number;
  /** Accepted for lucide-react compatibility; ignored. */
  absoluteStrokeWidth?: boolean;
}

export type AppIcon = React.FC<AppIconProps>;

// lucide-react icons default to 24px; react-icons default to 1em. Keep 24 so icons
// rendered without a size don't shrink to the surrounding font size.
function duotone(Icon: IconType, BoldIcon?: IconType): AppIcon {
  const Wrapped: AppIcon = ({ size = 24, strokeWidth, absoluteStrokeWidth, ...rest }) => {
    const Rendered = BoldIcon && Number(strokeWidth) >= 2.5 ? BoldIcon : Icon;
    return <Rendered size={size} {...rest} />;
  };
  Wrapped.displayName = Icon.name;
  return Wrapped;
}

export const Activity = duotone(PiPulseDuotone);
export const AlertCircle = duotone(PiWarningCircleDuotone);
export const AlertTriangle = duotone(PiWarningDuotone);
export const ArrowLeft = duotone(PiArrowLeftDuotone);
export const AtSign = duotone(PiAtDuotone);
export const Bot = duotone(PiRobotDuotone);
export const Calendar = duotone(PiCalendarDuotone);
export const CalendarRange = duotone(PiCalendarBlankDuotone);
export const Check = duotone(PiCheckDuotone, PiCheckBold);
export const CheckCircle = duotone(PiCheckCircleDuotone);
export const CheckCircle2 = duotone(PiCheckCircleDuotone);
export const ChevronDown = duotone(PiCaretDownDuotone);
export const ChevronLeft = duotone(PiCaretLeftDuotone);
export const ChevronRight = duotone(PiCaretRightDuotone);
export const ChevronUp = duotone(PiCaretUpDuotone);
export const ChevronsLeft = duotone(PiCaretDoubleLeftDuotone);
export const ChevronsRight = duotone(PiCaretDoubleRightDuotone);
export const Clock = duotone(PiClockDuotone);
export const Cloud = duotone(PiCloudDuotone);
export const Copy = duotone(PiCopyDuotone);
export const Download = duotone(PiDownloadSimpleDuotone);
export const Edit = duotone(PiPencilSimpleDuotone);
export const ExternalLink = duotone(PiArrowSquareOutDuotone);
export const Eye = duotone(PiEyeDuotone);
export const Folder = duotone(PiFolderDuotone);
export const Gamepad2 = duotone(PiGameControllerDuotone);
export const Gauge = duotone(PiGaugeDuotone);
export const GitBranch = duotone(PiGitBranchDuotone);
export const Globe = duotone(PiGlobeDuotone);
export const HeartPulse = duotone(PiHeartbeatDuotone);
export const Image = duotone(PiImageDuotone);
export const ImageOff = duotone(PiCameraSlashDuotone);
export const Info = duotone(PiInfoDuotone);
export const KeyRound = duotone(PiKeyDuotone);
export const Layers = duotone(PiStackDuotone);
export const Link2 = duotone(PiLinkDuotone);
export const List = duotone(PiListDuotone);
export const ListChecks = duotone(PiListChecksDuotone);
export const Loader2 = duotone(PiSpinnerGapDuotone);
export const LogIn = duotone(PiSignInDuotone);
export const LogOut = duotone(PiSignOutDuotone);
export const Mail = duotone(PiEnvelopeDuotone);
export const MapPin = duotone(PiMapPinDuotone);
export const MicStage = duotone(PiMicrophoneStageDuotone);
export const Minus = duotone(PiMinusDuotone);
export const Monitor = duotone(PiMonitorDuotone);
export const Music = duotone(PiMusicNotesDuotone);
export const Package = duotone(PiPackageDuotone);
export const Pause = duotone(PiPauseDuotone);
export const Play = duotone(PiPlayDuotone);
export const Plus = duotone(PiPlusDuotone);
export const Power = duotone(PiPowerDuotone);
export const Radar = duotone(PiBroadcastDuotone);
export const Radio = duotone(PiRadioDuotone);
export const RadioTower = duotone(PiWifiHighDuotone);
export const Receipt = duotone(PiReceiptDuotone);
export const RefreshCcw = duotone(PiArrowCounterClockwiseDuotone);
export const RefreshCw = duotone(PiArrowClockwiseDuotone);
export const RotateCcw = duotone(PiArrowCounterClockwiseDuotone);
export const Save = duotone(PiFloppyDiskDuotone);
export const Search = duotone(PiMagnifyingGlassDuotone);
export const Send = duotone(PiPaperPlaneTiltDuotone);
export const Server = duotone(PiHardDrivesDuotone);
export const Settings = duotone(PiGearSixDuotone);
export const Sliders = duotone(PiSlidersHorizontalDuotone);
export const Sparkles = duotone(PiSparkleDuotone);
export const Square = duotone(PiSquareDuotone);
export const Tag = duotone(PiTagDuotone);
export const Terminal = duotone(PiTerminalWindowDuotone);
export const Timer = duotone(PiTimerDuotone);
export const Trash2 = duotone(PiTrashDuotone);
export const Tv = duotone(PiTelevisionDuotone);
export const Upload = duotone(PiUploadSimpleDuotone);
export const UserCheck = duotone(PiUserSwitchDuotone);
export const Users = duotone(PiUsersDuotone);
export const UsersRound = duotone(PiUsersThreeDuotone);
export const Video = duotone(PiVideoCameraDuotone);
export const Wrench = duotone(PiWrenchDuotone);
export const X = duotone(PiXDuotone);
export const XCircle = duotone(PiXCircleDuotone);
export const Zap = duotone(PiLightningDuotone);
